import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Body1Strong, Caption1, makeStyles, tokens, } from '@fluentui/react-components';
import { ChevronRightRegular, HomeRegular, LockClosedRegular, } from '@fluentui/react-icons';
import { useNavigate } from 'react-router';
import { useAppConfig } from '#src/app/app-config.js';
import { pagePath } from '#src/app/navigation/module.js';
import { visibleModules } from '#src/app/navigation/navigation.js';
import { useAccess, useSession } from '#src/auth/session.js';
import { PageShell } from '#src/shared/ui/PageShell.js';
import { EmptyState } from '#src/shared/ui/StateView.js';
import { Card, IconTile } from '#src/shared/ui/Surface.js';
const useStyles = makeStyles({
    columns: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
        alignItems: 'start',
        gap: tokens.spacingHorizontalL,
        '@media (max-width: 1023px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        listStyleType: 'none',
        margin: 0,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        padding: tokens.spacingHorizontalM,
        boxSizing: 'border-box',
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke3}`,
        borderRadius: tokens.borderRadius2XLarge,
        color: 'inherit',
        fontFamily: 'inherit',
        textAlign: 'start',
        cursor: 'pointer',
        ':hover': {
            backgroundColor: tokens.colorBrandBackground2,
            border: `1px solid ${tokens.colorBrandStroke2}`,
        },
        ':focus-visible': {
            outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: '2px',
        },
    },
    text: { display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 },
    subtle: { color: tokens.colorNeutralForeground3 },
    account: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    roles: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
});
function Destination({ page, path }) {
    const styles = useStyles();
    const navigate = useNavigate();
    const Icon = page.icon;
    return (_jsx("li", { children: _jsxs("button", { type: "button", className: styles.row, onClick: () => navigate(path), children: [_jsx(IconTile, { children: _jsx(Icon, {}) }), _jsxs("span", { className: styles.text, children: [_jsx(Body1Strong, { children: page.title }), _jsx(Caption1, { className: styles.subtle, children: page.description })] }), _jsx(ChevronRightRegular, { className: styles.subtle })] }) }));
}
function AccountCard() {
    const styles = useStyles();
    const { principal } = useSession();
    if (principal.kind !== 'user') {
        return null;
    }
    return (_jsxs(Card, { title: "Sua conta", padded: true, children: [_jsxs("div", { className: styles.account, children: [_jsx(Body1Strong, { children: principal.name }), _jsx(Caption1, { className: styles.subtle, children: principal.email })] }), _jsx("div", { className: styles.roles, children: principal.roles.length === 0 ? (_jsx(Badge, { appearance: "outline", children: "Sem perfil" })) : (principal.roles.map((role) => (_jsx(Badge, { appearance: "tint", color: "brand", children: role }, role)))) })] }));
}
/** Início: as páginas que a pessoa pode abrir e o que ela pode fazer. */
export function HomePage() {
    const styles = useStyles();
    const { principal } = useSession();
    const { brand, modules } = useAppConfig();
    const destinations = visibleModules(modules, useAccess())
        .filter(({ module }) => module.placement === 'main')
        .flatMap(({ module, pages }) => pages.map((page) => ({ page, path: pagePath(module, page) })));
    const name = principal.kind === 'user' ? principal.name : principal.appId;
    const [firstName] = name.split(' ');
    return (_jsx(PageShell, { icon: _jsx(HomeRegular, {}), title: `Olá, ${firstName ?? name}`, description: brand.description, children: _jsxs("div", { className: styles.columns, children: [_jsx(Card, { title: "Por onde come\u00E7ar", children: destinations.length === 0 ? (_jsx(EmptyState, { icon: _jsx(LockClosedRegular, {}), title: "Voc\u00EA ainda n\u00E3o tem um perfil", description: "Por enquanto voc\u00EA v\u00EA s\u00F3 o que \u00E9 aberto a todos. Pe\u00E7a acesso a um administrador." })) : (_jsx("ul", { className: styles.list, children: destinations.map((destination) => (_jsx(Destination, { ...destination }, destination.path))) })) }), _jsx(AccountCard, {})] }) }));
}
//# sourceMappingURL=HomePage.js.map